import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Customer } from '@usf/customer-types';
import { MyOrdersStoreService, Order, orderSelectors } from '@usf/ngrx-order';
import { Observable, combineLatest } from 'rxjs';
import { take, filter } from 'rxjs/operators';
import {
  PanAppState,
  SelectedCustomerState,
  UserState,
} from '@panamax/app-state';

import { OrderService } from './order.service';
import { CustomerStoreService } from 'src/app/ngrx-customer/services';
import { MessageHandlerService } from 'src/app/shared/services/message/message-handler.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { PATHS } from '@shared/constants/paths';

@Injectable({
  providedIn: 'root',
})
export class SubmittedOrderResolverService {
  loading = false;
  selectedCustomer: Customer;

  constructor(
    protected panAppState: PanAppState,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderService: OrderService,
    private customerStoreService: CustomerStoreService,
    private messageHandlerService: MessageHandlerService,
    private store: Store<State>,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): any {
    const orderId = route.paramMap.get('orderId');
    this.myOrdersStoreService
      .getOrderWithId(orderId)
      .pipe(take(1))
      .subscribe(order => {
        if (!order) {
          this.switchToCustomerAndLoadOrder(orderId, true);
        } else {
          this.switchToCustomerAndLoadOrder(orderId, false);
        }
      });
  }

  switchToCustomerAndLoadOrder(orderId: string, checkOrderStatus: boolean) {
    const values = orderId ? orderId.split(':') : [];
    const customerNumber = parseInt(values[0]) ?? undefined;
    const departmentNumber = parseInt(values[1]) ?? undefined;
    const orderServerId = values[2] ?? undefined;

    if (
      (!customerNumber && customerNumber !== 0) ||
      (!departmentNumber && departmentNumber !== 0) ||
      !orderServerId
    ) {
      this.orderService.handleOrderDetailsPageError();
    }

    this.orderService.checkCustomerIsAvailable(customerNumber);

    this.customerStoreService
      .customerWithNumber$(customerNumber)
      .pipe(
        filter(customer => !!customer),
        take(1),
      )
      .subscribe(customer => {
        this.orderService.switchToCustomerIfNeeded(
          customer as Customer,
          departmentNumber,
        );

        this.panAppState.customer$
          .pipe(
            filter((currentCustomer: SelectedCustomerState) => {
              return (
                currentCustomer.customerNumber === customer.customerNumber &&
                (Number(currentCustomer.departmentNumber) ===
                  departmentNumber ||
                  Number(departmentNumber) === 0 ||
                  !this.orderService.customerHasDepartment(
                    customer,
                    Number(departmentNumber),
                  ))
              );
            }),

            take(1),
          )
          .subscribe(() => {
            if (checkOrderStatus) {
              this.orderService.checkOrderStatus(orderServerId);

              this.store
                .select(orderSelectors.selectOrderStatusInFlight())
                .pipe(
                  filter(fetchingOrder => !!fetchingOrder),
                  take(1),
                )
                .subscribe(() => {
                  this.selectOrderData(orderId);
                });
            } else {
              this.selectOrderData(orderId);
            }
          });
      });
  }

  shouldDoTandemUpdate(order: Order) {
    if (!order?.orderHeader?.lastRealtimeUpdate) {
      return true;
    }
    const lastUpdated = new Date(order?.orderHeader?.lastRealtimeUpdate);
    const now = new Date();
    const diff = now.getTime() - lastUpdated.getTime();
    const diffInMinutes = diff / 1000 / 60;
    return diffInMinutes > 5;
  }

  selectOrderData(orderId: string) {
    this.store
      .select(orderSelectors.selectOrderStatusInFlight())
      .pipe(
        filter(fetchingOrder => !fetchingOrder),
        take(1),
      )
      .subscribe(() => {
        this.panAppState.currentUrl$.pipe(take(1)).subscribe(url => {
          const currentUrl = url;
          if (orderId) {
            combineLatest([
              this.myOrdersStoreService.getOrderWithId(orderId),
              this.panAppState.user$,
            ])
              .pipe(
                filter(([order, user]) => !!user),
                take(1),
              )
              .subscribe(([order, user]) => {
                if (order && this.orderService.orderStatusOnWhitelist(order)) {
                  const loadTandemOrderData = this.shouldDoTandemUpdate(order);
                  this.loadProductsForOrder(order, user, loadTandemOrderData);
                } else {
                  this.orderService.handleOrderDetailsPageError();
                }
              });
          } else {
            this.orderService.handleOrderDetailsPageError();
          }
        });
      });
  }

  loadProductsForOrder(
    order: Order,
    user: UserState,
    loadTandemOrderData: boolean,
  ) {
    if (
      user.tmUser === 'Y' &&
      order?.orderHeader?.orderType !== 'VS' &&
      loadTandemOrderData
    ) {
      this.messageHandlerService
        .createSpinnerModal(true, PATHS.MY_ORDERS_VIEW)
        .then(() =>
          this.myOrdersStoreService.retrieveOrderDetails(
            order?.orderHeader.tandemOrderNumber,
            false,
          ),
        );
    } else {
      this.orderService.loadProductsForOrder(order);
    }
  }
}
